import { create } from 'zustand';
import type { DialableLead, ProtocolTemplate } from '../types';
import { dial as twilioDial, hangup, setMuted, ensureDevice, getActiveCall, teardown, onDeviceError } from '../lib/twilio-device';
import { supabase } from '../lib/supabase';
import { pruefeNummer } from '../lib/validation';
import { invalidateCache } from '../lib/useCachedQuery';
import { merkeZuletzt } from '../lib/zuletztBearbeitet';
import type { AnrufErgebnis } from '../lib/anrufErgebnis';

type DialerStatus = 'idle' | 'ready' | 'dialing' | 'in-call' | 'wrap-up' | 'paused';

interface AgentSettings {
  pause_between_calls_s: number;
  auto_voicemail_skip: boolean;
  retry_count: number;
  auto_advance: boolean;
  test_mode_enabled: boolean;
  test_mode_override_number: string | null;
}

const DEFAULT_SETTINGS: AgentSettings = {
  pause_between_calls_s: 2,
  auto_voicemail_skip: false,
  retry_count: 1,
  auto_advance: true,
  test_mode_enabled: false,
  test_mode_override_number: null,
};

// Ein Wählversuch bekommt einen eigenen Schlüssel, sobald er losgeht. Er ist
// die Klammer um alles, was danach zu diesem Versuch geschrieben wird —
// unabhängig davon, ob die Telefonanlage eine Anruf-Kennung liefert (bei einem
// gescheiterten Verbindungsaufbau tut sie das nicht).
function neuerVersuchsSchluessel(): string {
  const zufall = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
  return `v-${Date.now()}-${zufall}`;
}

/**
 * Hält einen Wählversuch fest — auch wenn niemand die Nachbereitung speichert.
 *
 * Destiny, 17.08.2026: „Im Dashboard aktualisieren sich die getätigten Anrufe
 * nicht." Gezählt wurde bis hierher nur, was von Hand gespeichert wurde: ein
 * „Verwerfen", ein geschlossener Tab oder ein gescheiterter Verbindungsaufbau
 * hinterließen nichts.
 */
export async function anrufFesthalten(opts: {
  customerId: string | null;
  attemptKey: string;
  callSid?: string | null;
  toNumber?: string | null;
  outcome?: AnrufErgebnis | null;
  durationS?: number | null;
  protocol?: unknown;
  disposition?: string | null;
  noteSummary?: string | null;
}): Promise<void> {
  const { error } = await supabase.rpc('log_call_attempt', {
    p_customer_id: opts.customerId,
    p_attempt_key: opts.attemptKey,
    p_call_sid: opts.callSid ?? null,
    p_to_number: opts.toNumber ?? null,
    p_outcome: opts.outcome ?? null,
    p_answered: opts.outcome ? opts.outcome === 'erreicht' : null,
    p_duration_s: opts.durationS ?? null,
    p_protocol: opts.protocol ?? null,
    p_disposition: opts.disposition ?? null,
    p_note_summary: opts.noteSummary ?? null,
  });
  if (error) throw error;
  // Kennzahlen und Arbeitslisten sofort nachziehen — „Anrufe heute" soll nicht
  // erst nach einem Neuladen stimmen.
  invalidateCache((k) =>
    k === 'dashboard' || k.startsWith('dashboard-intake:') || k.startsWith('queue:')
    || k.startsWith('customers:') || k.startsWith('callbacks:') || k.startsWith('history:')
    || (!!opts.customerId && k === `customer:${opts.customerId}`));
}

// Versuch festhalten, ohne den Anruf-Ablauf zu gefährden: ein Fehler beim
// Protokollieren darf niemals dazu führen, dass die Nachbereitung nicht öffnet.
async function versuchFesthalten(
  attemptKey: string,
  customerId: string | null,
  toNumber: string,
  outcome: AnrufErgebnis | null,
): Promise<void> {
  try {
    await anrufFesthalten({
      customerId, attemptKey, toNumber, outcome,
      callSid: useDialerStore.getState().callSid,
    });
  } catch (err) {
    console.error('Anrufversuch konnte nicht protokolliert werden', err);
  }
}

interface DialerState {
  status: DialerStatus;
  queue: DialableLead[];
  current: DialableLead | null;
  muted: boolean;
  callSid: string | null;
  /** Schlüssel des laufenden Wählversuchs (s. neuerVersuchsSchluessel). */
  attemptKey: string | null;
  /** Tatsächlich gewählte Nummer in internationaler Schreibweise. */
  dialedNumber: string | null;
  // Zähler pro Wählversuch. Die Anruf-Kennung (callSid) kommt erst verzögert
  // von der Telefonanlage und ist deshalb kein verlässliches „neuer Anruf"-
  // Signal — das Skript-Fenster setzt sich an diesem Zähler zurück.
  callAttempt: number;
  callStartedAt: number | null;        // ms epoch — set when status -> in-call
  connecting: boolean;                 // true while ensureDevice() is registering
  error: string | null;
  settings: AgentSettings;
  protocolTemplate: ProtocolTemplate | null;
  // Org-wide test mode (set by an admin). Overrides everything: when enabled,
  // every dial goes to `number`. Enforced server-side too; mirrored here for
  // the dialer banner and the dialled-number display.
  globalTest: { enabled: boolean; number: string | null };
  // Anruffenster klein gemacht? Steht hier und nicht im Fenster selbst, weil
  // sich der Power Dialer danach richten muss: klein gemacht liegt das Fenster
  // als Leiste unten rechts — genau dort, wo sonst der Dialer sitzt. Ohne diese
  // Absprache lagen beide übereinander.
  callWindowCollapsed: boolean;

  enqueue: (leads: DialableLead[]) => void;
  removeFromQueue: (customerId: string) => void;
  clearQueue: () => void;

  start: () => Promise<void>;
  stop: () => Promise<void>;
  dialAdHoc: (number: string) => Promise<void>;
  pause: () => void;
  resume: () => void;
  next: () => Promise<void>;
  endCurrent: () => void;
  toggleMute: () => void;
  setCallWindowCollapsed: (v: boolean) => void;
  setError: (err: string | null) => void;
  loadSettings: () => Promise<void>;
  loadProtocolTemplate: () => Promise<void>;
  loadGlobalTest: () => Promise<void>;
}

export const useDialerStore = create<DialerState>((set, get) => ({
  status: 'idle',
  queue: [],
  current: null,
  muted: false,
  callSid: null,
  attemptKey: null,
  dialedNumber: null,
  callAttempt: 0,
  callStartedAt: null,
  connecting: false,
  error: null,
  settings: DEFAULT_SETTINGS,
  protocolTemplate: null,
  globalTest: { enabled: false, number: null },
  callWindowCollapsed: false,

  setCallWindowCollapsed: (v) => set({ callWindowCollapsed: v }),

  enqueue: (leads) =>
    set((s) => ({
      queue: [...s.queue, ...leads.filter((l) => !s.queue.find((q) => q.customer_id === l.customer_id))],
    })),
  removeFromQueue: (customerId) =>
    set((s) => ({ queue: s.queue.filter((l) => l.customer_id !== customerId) })),
  clearQueue: () => set({ queue: [] }),

  loadSettings: async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from('agent_settings')
      .select('pause_between_calls_s,auto_voicemail_skip,retry_count,auto_advance,test_mode_enabled,test_mode_override_number')
      .eq('user_id', user.id)
      .maybeSingle();
    if (data) set({ settings: { ...DEFAULT_SETTINGS, ...data } as AgentSettings });
  },

  loadProtocolTemplate: async () => {
    const { data, error } = await supabase.rpc('my_protocol_template');
    if (!error && Array.isArray(data) && data.length > 0) {
      set({ protocolTemplate: data[0] as ProtocolTemplate });
    }
  },

  loadGlobalTest: async () => {
    const { data } = await supabase
      .from('global_settings')
      .select('test_mode_enabled, test_mode_number')
      .eq('id', 1)
      .maybeSingle();
    const row = data as { test_mode_enabled?: boolean; test_mode_number?: string | null } | null;
    set({ globalTest: { enabled: !!row?.test_mode_enabled, number: row?.test_mode_number ?? null } });
  },

  start: async () => {
    // Re-Entrancy-Sperre: ein zweiter Klick auf Start, während der erste
    // Verbindungsaufbau noch läuft, hat vorher einen zweiten, parallelen
    // Registrierungsversuch losgetreten — beide sind dann unabhängig
    // gescheitert und mit je einer eigenen Fehlermeldung überschrieben ("zwei
    // Fehlermeldungen, kein Anruf zustande gekommen", Sharina Juli 2026).
    if (get().connecting) return;
    // Aus einem hängen gebliebenen Zustand (Status sagt "im Gespräch", aber es
    // gibt gar keinen aktiven Call) selbst befreien — vorher tat der
    // Start-Knopf dann einfach nichts, bis man die Seite neu geladen hat.
    const { status } = get();
    if ((status === 'in-call' || status === 'dialing') && !getActiveCall()) {
      set({ status: 'idle', callSid: null, callStartedAt: null });
    }
    set({ error: null, connecting: true });
    try {
      await Promise.all([get().loadSettings(), get().loadProtocolTemplate(), get().loadGlobalTest()]);
      await ensureDevice();
      set({ status: 'ready', error: null });
      await get().next();
    } catch (err) {
      set({ error: (err as Error).message, status: 'idle' });
    } finally {
      set({ connecting: false });
    }
  },

  // Power Dialer komplett beenden: auflegen, Twilio-Verbindung abbauen und
  // das Panel schließen (Status idle + leere Queue blendet es aus).
  stop: async () => {
    try { await teardown(); } catch { /* best effort */ }
    set({
      status: 'idle', queue: [], current: null, muted: false,
      callSid: null, attemptKey: null, dialedNumber: null,
      callStartedAt: null, connecting: false, error: null,
    });
  },

  // Direct dial of an arbitrary number not in the system (walk-ins). Logged as
  // a normal call with no customer attached; honours the global test mode.
  dialAdHoc: async (number) => {
    const n = number.trim();
    if (!n) return;
    const { status } = get();
    if (status === 'dialing' || status === 'in-call') {
      set({ error: 'Erst das laufende Gespräch beenden.' });
      return;
    }
    const lead: DialableLead = {
      customer_id: `adhoc-${Date.now()}`,
      full_name: `Direktanruf ${n}`,
      phone_number: n,
      status: '', not_reached_count: 0, school_id: null, school_name: null,
      source: 'lead', last_contact_at: null, ad_hoc: true,
    };
    set((s) => ({ queue: [lead, ...s.queue] }));
    await get().start();
  },

  pause: () => set({ status: 'paused' }),
  resume: () => {
    if (get().status === 'paused') {
      set({ status: 'ready' });
      void get().next();
    }
  },

  next: async () => {
    const { queue, status, settings, globalTest } = get();
    if (status === 'paused' || status === 'in-call' || status === 'dialing') return;
    const lead = queue[0];
    if (!lead) {
      set({ status: 'ready', current: null });
      return;
    }
    // If we're coming out of wrap-up, honour the configured pause.
    if (status === 'wrap-up' && settings.pause_between_calls_s > 0) {
      await new Promise((r) => setTimeout(r, settings.pause_between_calls_s * 1000));
    }
    const attemptKey = neuerVersuchsSchluessel();
    set((s) => ({
      status: 'dialing', current: lead, callSid: null, callStartedAt: null, error: null,
      attemptKey, dialedNumber: null,
      callAttempt: s.callAttempt + 1,
    }));
    // Für den Wiedereinstieg nach dem Gespräch („wer war das gerade?").
    merkeZuletzt(lead.ad_hoc ? null : lead.customer_id, lead.full_name, 'anruf');
    try {
      // Test-mode override: route every call to a chosen number instead of the
      // lead's real one. CustomerId still flows through, so call_log attribution
      // stays correct. The org-wide switch wins over the per-agent one; both are
      // also enforced server-side in the twilio-voice edge function.
      const rohNummer =
        globalTest.enabled && globalTest.number
          ? globalTest.number
          : settings.test_mode_enabled && settings.test_mode_override_number
            ? settings.test_mode_override_number
            : lead.phone_number;
      // 84 % der Nummern im Bestand stehen national („017…"). Genau dieser Text
      // wurde bisher gewählt — die Telefonanlage lehnt ihn ab („invalid phone
      // number"), der Anruf kam nie zustande und der Versuch blieb nirgends
      // stehen (Destiny, 17.08.2026). Deshalb hier auf +49… bringen; die
      // Telefonie-Function tut dasselbe noch einmal serverseitig.
      // Zusätzlich zur Schreibweise wird die Nummer auf Plausibilität geprüft:
      // eine doppelte Vorwahl wird herausgenommen („+49 49 176…" — 303 solcher
      // Nummern im Bestand, Meldung vom 28.08.2026), und eine Nummer mit
      // offensichtlich falscher Ziffernzahl wird gar nicht erst gewählt. Vorher
      // ging so ein Anruf hinaus, scheiterte an der Anlage und verbrauchte
      // einen Versuch, ohne dass jemand den Grund sah.
      const befund = pruefeNummer(rohNummer ?? '');
      const dialNumber = befund.nummer;
      const cid = lead.ad_hoc ? undefined : (lead.customer_id || undefined);
      if (!befund.waehlbar) {
        // Unvollständige Nummer: nicht wählen, aber als Versuch festhalten —
        // sonst bleibt unsichtbar, warum bei dieser Person nichts passiert.
        let notiert = true;
        await anrufFesthalten({
          customerId: cid ?? null, attemptKey, toNumber: rohNummer ?? null,
          outcome: 'nicht_waehlbar',
        }).catch((err) => {
          // Protokoll darf den Ablauf nicht blockieren — aber die Meldung darf
          // dann auch nicht behaupten, es stünde im Verlauf.
          notiert = false;
          console.error('Nicht wählbare Nummer konnte nicht protokolliert werden', err);
        });
        set((s) => ({
          status: 'ready',
          queue: s.queue.slice(1),
          error: `Diese Nummer kann nicht gewählt werden: „${rohNummer ?? '—'}". `
            + (befund.hinweis ? befund.hinweis + ' ' : '')
            + 'Bitte im Kundenprofil korrigieren.'
            + (notiert ? ' Der Versuch ist im Verlauf notiert.' : ''),
        }));
        return;
      }
      set({ dialedNumber: dialNumber });
      // Ad-hoc dials carry a synthetic id for queue handling only — never send
      // it to Twilio (it isn't a real customer uuid).
      const call = await twilioDial(dialNumber, cid, attemptKey);
      // Die Anruf-Kennung (CallSid) vergibt die Telefonanlage, nicht der
      // Browser: direkt nach dem Verbindungsaufbau ist sie noch leer und
      // trifft erst mit „klingelt" bzw. „abgenommen" ein. Vorher wurde genau
      // dieser leere Wert übernommen und nie nachgezogen — die Nachbereitung
      // („Speichern + Weiter") hatte damit nichts, woran sie das Gespräch
      // hängen konnte, und übersprang das Speichern von Skript und Notizen
      // stillschweigend (Sharina, 17.08.2026).
      const sidUebernehmen = () => {
        const sid = call.parameters?.CallSid;
        if (sid && get().callSid !== sid) set({ callSid: sid });
      };
      // Sicherheitsnetz für Verläufe, in denen kein passendes Ereignis kommt:
      // ein paar Sekunden nachfassen, statt die Kennung zu verlieren.
      let versuche = 0;
      const nachfassen = window.setInterval(() => {
        sidUebernehmen();
        if (get().callSid || ++versuche >= 20) window.clearInterval(nachfassen);
      }, 500);
      call.on('ringing', sidUebernehmen);
      call.on('accept', () => {
        sidUebernehmen();
        set({ status: 'in-call', callStartedAt: Date.now() });
      });
      call.on('disconnect', () => {
        sidUebernehmen();
        window.clearInterval(nachfassen);
        set((s) => ({ status: 'wrap-up', queue: s.queue.slice(1) }));
        // Der Versuch wird SOFORT festgehalten, noch vor der Nachbereitung.
        // Damit zählt er auch dann, wenn danach „Verwerfen" gedrückt oder der
        // Tab geschlossen wird. Das Ergebnis kommt beim Speichern dazu.
        void versuchFesthalten(attemptKey, cid ?? null, dialNumber, null);
      });
      call.on('cancel', () => {
        window.clearInterval(nachfassen);
        void versuchFesthalten(attemptKey, cid ?? null, dialNumber, 'niemand');
      });
      call.on('error', (e) => {
        window.clearInterval(nachfassen);
        set({ error: e.message });
        void versuchFesthalten(attemptKey, cid ?? null, dialNumber, 'fehlgeschlagen');
      });
    } catch (err) {
      set({ error: (err as Error).message, status: 'ready' });
    }
  },

  endCurrent: () => {
    hangup();
    set({ status: 'wrap-up' });
  },

  toggleMute: () => {
    const next = !get().muted;
    setMuted(next);
    set({ muted: next });
  },

  setError: (err) => set({ error: err }),
}));

// Device-level Twilio-Fehler (z. B. abgelehnte Registrierung, Medien-/ICE-
// Probleme) gingen vorher nur ins Konsolen-Log — jetzt sichtbar im Dialer,
// statt dass ein Anruf ohne erkennbaren Grund einfach nicht zustande kommt.
//
// `connecting` bleibt hier bewusst unangetastet: das Flag gehört start() und
// sperrt den Start-Knopf gegen Doppelklicks. Wurde es von hier aus
// zurückgesetzt, war der Knopf mitten im Verbindungsaufbau wieder klickbar —
// genau die Doppelklick-Sperre, die es zu verhindern gilt, war damit ausgehebelt
// und jeder weitere Klick schickte eine zusätzliche Anfrage los.
onDeviceError((msg) => useDialerStore.setState({ error: msg, status: 'idle' }));
